import React, {useEffect, useState} from 'react';
import {
  Platform,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
  StyleSheet,
  BackHandler,
} from 'react-native';
import {useFocusEffect} from '@react-navigation/native';
import ButtonComponent from '../../components/ButtonComponent/ButtonComponent';
import Toast from '../../components/Toast/Toast';
import {styles} from './styles';
import {useNavigation} from '@react-navigation/native';
import AntDesign from 'react-native-vector-icons/AntDesign';
import {NavigationProps} from '../../navigation/Routes';
import {AppRoutes} from '../../navigation/Routes.types';
import moment from 'moment';
import 'moment/locale/pt-br';
import {useCompanyStore} from '../../store/CompanyStore';
import {Colors} from '../../constants/Colors';
import NPSService from '../../services/NPSService/NPSService';
import ProfileService from '../../services/ProfileService/ProfileService';
import {useAuthStore} from '../../store/AuthStore';
import NPSModal from '../Home/components/NPSModal/NPSModal';
import NotificationPermissionModal from '../../components/NotificationPermissionModal/NotificationPermissionModal';
import {OneSignal} from 'react-native-onesignal';
import {
  getAnalytics,
  logEvent,
  logPurchase,
} from '@react-native-firebase/analytics';

moment.locale('pt-br');

const OrderSent = ({route}) => {
  const {goBack} = useNavigation();
  const {navigate} = useNavigation<any>();
  const [toast, setToast] = useState<boolean>(true);
  const companies = useCompanyStore(state => state.companies);
  const {current_client, clientCode, clients} = useAuthStore().user;

  const data = route?.params;
  const paymentType = route?.params?.type;

  let orderNumber, deliveryDate, price, statusType, message, errorMessage;

  if (paymentType === 'credit_card') {
    // (credit_card)
    const isSuccess = data?.success === true;
    statusType = isSuccess ? 'S' : 'E';

    orderNumber =
      data?.data?.reference_id || data?.data?.request_payload?.reference_id;

    deliveryDate = null;

    price = null;

    message = isSuccess ? data?.message : data?.message;
    errorMessage = !isSuccess
      ? data?.message || 'Erro ao processar pagamento'
      : '';
  } else {
    // (ticket/boleto)
    price = String(route?.params?.details?.VALOR);
    statusType = route?.params?.details?.TYPE || 'E';
    orderNumber = data?.details?.ORDEM;
    deliveryDate = data?.details?.ENTREGA;
    message = route?.params?.details?.TYPE === 'E' && data?.details;
    errorMessage =
      route?.params?.data?.message || route?.params?.data?.details || '';
  }

  const analyticsInstance = getAnalytics();

  useEffect(() => {
    if (statusType === 'S') {
      const purchaseValue = price ? parseFloat(price) : 0;

      if (Platform.OS !== 'web') {
        logPurchase(analyticsInstance, {
          transactionId: orderNumber,
          value: purchaseValue,
          currency: 'BRL',
        });
      } else {
        logEvent(analyticsInstance, 'purchase', {
          transaction_id: orderNumber,
          value: purchaseValue,
          currency: 'BRL',
        });
      }

      logEvent(analyticsInstance, 'order_sent', {
        client_code: current_client?.clientCode || clientCode,
        order_number: orderNumber,
        order_value: purchaseValue,
        payment_type: paymentType,
      });
    } else {
      logEvent(analyticsInstance, 'order_error', {
        client_code: current_client?.clientCode || clientCode,
        error_message: errorMessage,
        payment_type: paymentType,
      });
    }
  }, [statusType]);

  useFocusEffect(
    React.useCallback(() => {
      const onBackPress = () => {
        if (statusType === 'S') {
          navigate(AppRoutes.HOME, {cleanFilters: true, npsVerify: showNPS});
          return true;
        } else {
          goBack();
          return true;
        }
      };

      // registra listener
      const subscription = BackHandler.addEventListener(
        'hardwareBackPress',
        onBackPress,
      );

      // cleanup
      return () => {
        subscription.remove();
      };
    }, []),
  );

  const [showNPS, setShowNPS] = useState<boolean>(false);
  const [showNotifModal, setShowNotifModal] = useState<boolean>(false);

  // iOS só exibe um <Modal> por vez: aguarda o loading do Checkout fechar
  const openModalWithDelay = (open: () => void) => {
    setTimeout(open, Platform.OS === 'ios' ? 800 : 0);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setToast(false);
      clearInterval(interval);
    }, 5000);

    // Decide qual (se algum) modal exibir após um pedido bem-sucedido.
    // Prioridade: NPS > permissão de notificação (mutuamente exclusivos — RN-03/04).
    const verifyPostOrderModal = async () => {
      if (statusType !== 'S') {
        return;
      }

      const NPSverify = await NPSService.verifyNPS(
        current_client?.clientCode || clientCode,
      );
      if (NPSverify) {
        openModalWithDelay(() => setShowNPS(true));
        return;
      }

      // RN-01: só para quem NÃO concedeu permissão de push.
      // RN-02: 40% de chance ao finalizar o pedido.
      const granted = await OneSignal.Notifications.getPermissionAsync();
      if (!granted && Math.random() < 0.4) {
        logEvent(analyticsInstance, 'notification_prompt_shown', {
          client_code: current_client?.clientCode || clientCode,
        });
        openModalWithDelay(() => setShowNotifModal(true));
      }
    };
    verifyPostOrderModal();

    return () => clearInterval(interval);
  }, []);

  const handleAllowNotifications = async () => {
    try {
      const accepted = await OneSignal.Notifications.requestPermission(true);
      const playerId = await OneSignal.User.pushSubscription.getIdAsync();
      const code = current_client?.clientCode || clientCode;
      if (accepted && playerId && code) {
        await ProfileService.createOneSignalSubscription(playerId, code);
      }
      logEvent(analyticsInstance, 'notification_prompt_result', {
        client_code: code,
        result: accepted ? 'permitir' : 'negado',
      });
    } catch (e) {
      console.log(e);
    } finally {
      setShowNotifModal(false);
    }
  };

  const handleDismissNotifications = () => {
    logEvent(analyticsInstance, 'notification_prompt_result', {
      client_code: current_client?.clientCode || clientCode,
      result: 'agora_nao',
    });
    setShowNotifModal(false);
  };

  const handleNavigateHome = () => {
    (navigate as any)(AppRoutes.MAIN_TAB_SCREEN, {
      screen: AppRoutes.HOME,
      params: {
        cleanFilters: true,
        npsVerify: showNPS,
      },
    });
  };

  const handleNavigateBag = () => {
    (navigate as any)(AppRoutes.MAIN_TAB_SCREEN, {
      screen: AppRoutes.HOME,
    });
  };

  return (
    <View style={styles.container}>
      {statusType === 'S' && toast && (
        <Toast title="Seu pedido foi processado com sucesso!" />
      )}
      {statusType === 'S' ? (
        <ScrollView>
          <View style={styles.spaceXY}>
            <TouchableOpacity
              onPress={handleNavigateHome}
              style={{position: 'absolute', right: 30, top: 50}}>
              <AntDesign name="close" color="lightgray" size={26} />
            </TouchableOpacity>

            <Text style={styles.title}>Pedido enviado!</Text>

            {/* Cartão 1 - Número do pedido */}
            <View style={design.card}>
              <Text style={design.cardTitle}>Número do pedido</Text>
              <Text style={design.cardValue}>{orderNumber}</Text>
            </View>

            {/* Cartão 2 - Data provável da entrega (só exibe se tiver) */}
            {deliveryDate && (
              <View style={design.card}>
                <Text style={design.cardTitle}>Data provável da entrega</Text>
                <Text style={design.cardValue}>
                  {moment(deliveryDate, 'YYYYMMDD').format('DD/MM/YYYY (dddd)')}
                </Text>
              </View>
            )}

            {/* Cartão 3 - Valor (só exibe se tiver) */}
            {price && (
              <View style={design.card}>
                <Text style={design.cardTitle}>Valor total</Text>
                <Text style={design.cardHighlight}>
                  R${' '}
                  {Number(price).toLocaleString('pt-BR', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Text>
              </View>
            )}

            {/* Cartão adicional para pagamentos com cartão/pix */}
            {(paymentType === 'credit_card' || paymentType === 'pix') && (
              <View style={design.card}>
                <Text style={design.cardTitle}>Forma de pagamento</Text>
                <Text style={design.cardValue}>
                  {paymentType === 'credit_card' ? 'Cartão de Crédito' : 'PIX'}
                </Text>
                {paymentType === 'credit_card' && data?.data?.installment && (
                  <Text style={design.cardSubValue}>
                    {data.data.installment}x no cartão
                  </Text>
                )}
                {data?.data?.transaction_id && (
                  <Text style={design.cardSubValue}>
                    ID da transação: {data.data.transaction_id}
                  </Text>
                )}
              </View>
            )}

            {/* Mensagem de sucesso adicional */}
            {message && (
              <View style={design.infoBox}>
                <Text style={design.infoText}>{message}</Text>
              </View>
            )}

            {/* Botão de Acompanhar Pedidos */}
            <View style={design.actionButtonContainer}>
              <TouchableOpacity
                onPress={() => {
                  navigate(AppRoutes.MAIN_TAB_SCREEN, {
                    screen: AppRoutes.ORDERS,
                    params: {
                      force: true,
                    },
                  });
                }}
                activeOpacity={0.8}
                style={design.actionButton}>
                <AntDesign
                  name="profile"
                  size={20}
                  color="#FFF"
                  style={{marginRight: 8}}
                />
                <Text style={design.actionButtonText}>Acompanhar Pedidos</Text>
              </TouchableOpacity>
            </View>

            {clients?.length > 1 && (
              <>
                <View
                  style={{
                    marginTop: 30,
                    marginBottom: 50,
                    alignItems: 'center',
                    width: '80%',
                  }}>
                  <Text style={styles.quest}>
                    Que tal aproveitar e fazer um pedido em outra empresa
                    também?
                  </Text>
                  <TouchableOpacity
                    activeOpacity={0.8}
                    style={design.actionButton}
                    onPress={() => {
                      navigate(AppRoutes.HOME, {companyModal: true});
                    }}>
                    <AntDesign
                      name="plus"
                      size={20}
                      color="#FFF"
                      style={{marginRight: 8}}
                    />
                    <Text style={design.actionButtonText}>
                      Fazer Pedido em outra Empresa
                    </Text>
                  </TouchableOpacity>
                </View>
              </>
            )}

            {/* Rodapé - voltar à página inicial */}
            <View style={{marginTop: 20}}>
              <TouchableOpacity onPress={handleNavigateHome}>
                <Text style={{color: Colors.RedPallete.red, fontSize: 16}}>
                  Voltar à Página Inicial
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      ) : (
        <ScrollView>
          <View style={styles.spaceXY}>
            <TouchableOpacity
              onPress={handleNavigateBag}
              style={{position: 'absolute', right: 30, top: 50}}>
              <AntDesign name="close" color="lightgray" size={26} />
            </TouchableOpacity>

            <Text style={styles.title}>Erro ao enviar pedido!</Text>

            <Text style={styles.description}>
              Ocorreu um erro ao processar seu pedido.
            </Text>

            {message && <Text style={styles.quest}>{message}</Text>}

            {!!errorMessage ? (
              <Text style={styles.quest}>{errorMessage}</Text>
            ) : (
              <Text style={styles.quest}>
                Por favor, tente novamente mais tarde.
              </Text>
            )}

            <View style={{marginTop: 50}}>
              <TouchableOpacity onPress={handleNavigateHome}>
                <Text style={{color: Colors.RedPallete.red, fontSize: 16}}>
                  Voltar à Página Inicial
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </ScrollView>
      )}
      <NPSModal
        visible={showNPS}
        onClose={() => setShowNPS(false)}
        onSurvey={() => {
          navigate(AppRoutes.NPS);
          setShowNPS(false);
        }}
      />
      <NotificationPermissionModal
        visible={showNotifModal}
        onClose={handleDismissNotifications}
        onAllow={handleAllowNotifications}
      />
    </View>
  );
};

const design = StyleSheet.create({
  card: {
    marginVertical: 10,
    backgroundColor: '#FFF',
    borderRadius: 8,
    padding: 16,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
    width: '100%',
  },
  cardTitle: {
    fontSize: 16,
    color: Colors.GrayPallete.lightgrayThree,
    marginBottom: 5,
    fontFamily: 'TCCC-UnityText Bold',
  },
  cardValue: {
    fontSize: 18,
    fontFamily: 'TCCC-UnityHeadline Regular',
    color: 'black',
  },
  cardSubValue: {
    fontSize: 14,
    fontFamily: 'TCCC-UnityText Regular',
    color: '#666',
    marginTop: 4,
  },
  cardHighlight: {
    fontSize: 20,
    fontFamily: 'TCCC-UnityHeadline Regular',
    color: '#008000', // Verde para destacar sucesso
  },
  infoBox: {
    marginVertical: 10,
    backgroundColor: '#E8F5E9',
    borderRadius: 8,
    padding: 16,
    width: '100%',
  },
  infoText: {
    fontSize: 14,
    fontFamily: 'TCCC-UnityText Regular',
    color: '#2E7D32',
  },
  actionButtonContainer: {
    marginTop: 20,
    width: '100%',
    alignItems: 'center',
  },
  actionButton: {
    backgroundColor: Colors.RedPallete.red,
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 8,
  },
  actionButtonText: {
    color: '#FFF',
    fontSize: 16,
    fontFamily: 'TCCC-UnityHeadline Regular',
  },
  sectionTitle: {
    fontSize: 16,
    fontFamily: 'TCCC-UnityText Bold',
    marginBottom: 10,
    color: 'black',
  },
});

export default OrderSent;
